import _ from "lodash";

import { NPSEntity } from "../../entity/NPSEntity";
import { SchedulerEntity } from "../../entity/SchedulerEntity";
import { CollectionAdapter } from "../../main/adapters/CollectionAdapter/CollectionAdapter";
import { HttpAdapter } from "../../main/adapters/HttpAdapter";

const NpsHistoric = new Mongo.Collection("NpsHistoric", {
    connection: null
});

export class NPSGateway extends CollectionAdapter {
    HttpAdapter = new HttpAdapter();
    baseUrlNps = `${publicSettings.aws.baseUrls.assessmentCentre}/nps`;

    constructor(accountingId) {
        super(NpsHistoric);
        this.accountingId = accountingId;
    }

    async send({
        accountingId,
        companyId,
        senderEmail,
        ratingIds,
        resend,
        contactIds
    } = {}) {
        if (!accountingId || !senderEmail) throw new Error("Params invalid");
        return await this.HttpAdapter.call(`${this.baseUrlNps}/send-one`, {
            accountingId,
            companyId,
            senderEmail,
            ratingIds,
            resend,
            contactIds
        });
    }

    async sendMany({ accountingId, companyIds, username }) {
        if (!accountingId || !companyIds?.length || !username)
            throw new Error("Params invalid");
        return await this.HttpAdapter.call(`${this.baseUrlNps}/send-many`, {
            accountingId,
            companyIds,
            username
        });
    }

    /**
     * @name getNpsByPeriod
     * @public
     * Pegar os dados do NPS conforme o periodo
     * @param {Number} year - ano do periodo
     * @param {Number} quarter - semestre do periodo
     *
     * @returns {Promise<Object>} - retorna os dados conforme o periodo
     */

    async getNpsByPeriod(year, quarter) {
        const response = await this.HttpAdapter.call(
            `${this.baseUrlNps}/get-by-period`,
            {
                accountingId: this.accountingId,
                year: Number(year),
                quarter: Number(quarter)
            }
        );
        const npsEntity = new NPSEntity(response || {});
        return npsEntity;
    }

    async cancelScheduler(companyId) {
        if (!companyId) throw new Error("Params invalid");

        return await this.HttpAdapter.call(
            `${this.baseUrlNps}/cancel-scheduler-for-company`,
            {
                accountingId: this.accountingId,
                companyId
            }
        );
    }

    /*
     * @name setScoreRating
     * @public
     * Altera valor do score
     * @param { String } _id - Id do NPS
     * @param { Number } score - Score do NPS
     * @return { Object } - Resposta
     */
    async setScoreRating({ _id, score }) {
        if (!_id || !score) throw new Error("Invalid params.");

        return await this.HttpAdapter.call(
            `${this.baseUrlNps}/set-score-rating`,
            { _id, score }
        );
    }

    /**
     * @name setCommentedRating
     * @public
     * Altera valor do score
     * @param { String } _id - Id do NPS
     * @param { String } comment - Comentário do NPS
     * @return { Object } - Resposta
     */
    async setCommentedRating({ _id, comment } = {}) {
        if (!_id || !comment) throw new Error("Invalid params.");

        return await this.HttpAdapter.call(
            `${this.baseUrlNps}/set-commented-rating`,
            { _id, comment }
        );
    }

    /**
     * @name getScheduler
     * @public
     * Retornar os ids de empresas envidas do nps
     * @param {string} accountingId - id da contabilidade
     * @returns
     */
    async getScheduler(accountingId) {
        try {
            if (!accountingId) throw new Error("Invalid params");

            const data = await this.HttpAdapter.call(
                `${this.baseUrlNps}/get-scheduler-by-accountingid`,
                { accountingId }
            );

            return data.mensage ? null : new SchedulerEntity(data);
        } catch (err) {
            return null;
        }
    }

    /**
     * @name setRecommendationsRating
     * @public
     * Altera valor do score
     * @param { String } _id - Id do NPS
     * @param { String } comment - Comentário do NPS
     * @return { Object } - Resposta
     */
    async setRecommendationsRating({ _id, recommendations } = {}) {
        if (!_id || !recommendations) throw new Error("Invalid params.");

        return await this.HttpAdapter.call(
            `${this.baseUrlNps}/set-recommendations-rating`,
            { _id, recommendations }
        );
    }

    async wasAnsweredRating(_id) {
        if (!_id) throw new Error("Invalid params.");

        const response = await this.HttpAdapter.call(
            `${this.baseUrlNps}/was-answered-rating`,
            { _id }
        );

        return _.get(response, "answered");
    }

    async getLastRatingByCompany(companyId) {
        if (!companyId) throw new Error("Invalid params");

        const getLast = await this.HttpAdapter.call(
            `${this.baseUrlNps}/get-last-rating-by-companyid`,
            { companyId }
        );
        return getLast;
    }

    async getNPSHistoric({ accountingId, year, quarter }) {
        if (!accountingId || !year || !quarter)
            throw new Error("Invalid params");

        return await this.HttpAdapter.call(
            `${this.baseUrlNps}/get-nps-historic`,
            { accountingId, year, quarter }
        );
    }

    async populateNPSData({ accountingId, year, quarter }) {
        this.deleteMany({ "nps.accountingId": accountingId });

        const response = await this.getNPSHistoric({
            accountingId,
            year,
            quarter
        });

        const npsList = _.get(response, "npsList") || [];

        this.createMany(npsList);

        return response;
    }
}
